import { useMemo } from "react";

import { UseLuminanceArgs } from "./useLuminance.decl";

const WHITE = "#ffffff";
const BLACK = "#000000";

// Using this article's code "https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o"
function hexToRgb(hex: string) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const replaced = hex.replace(
    shorthandRegex,
    (_, r, g, b) => r + r + g + g + b + b
  );

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(replaced);

  if (!result) {
    return null;
  }

  const [_, r, g, b] = result;

  // From base 16 to base 10
  return {
    r: parseInt(r, 16),
    g: parseInt(g, 16),
    b: parseInt(b, 16),
  };
}

function luminance(r: number, g: number, b: number) {
  const luminances = [r, g, b].map((v) => {
    const value = v / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });

  const [redLuminance, greenLuminance, blueLuminance] = luminances;

  return (
    redLuminance * 0.2126 + greenLuminance * 0.7152 + blueLuminance * 0.0722
  );
}

const FALLBACK_RGB_COLOR = { r: 0, g: 179, b: 136 };
const FALLBACK_COLOR = "#00b388";

/**
 * Custom hook to determine text color and background color based on luminance.
 *
 * @param {Object} args - The arguments for the hook.
 * @param {string} args.color - The color to analyze for luminance.
 * @param {number} args.threshold - The threshold for determining text color.
 *
 * @returns {Object} - An object containing textColor and bgColor based on luminance analysis.
 *   - textColor {string} - The determined text color (either WHITE or BLACK).
 *   - bgColor {string} - The background color, using the input color or a fallback if input color is not valid
 */
export function useLuminance({ color, threshold }: UseLuminanceArgs) {
  // Transform the input color to RGB format
  const transformedColor = useMemo(() => hexToRgb(color), [color]);
  const rgbColor = useMemo(
    () => transformedColor ?? FALLBACK_RGB_COLOR,
    [transformedColor]
  );
  const whiteRgb = useMemo(() => hexToRgb(WHITE), []);

  const tagLuminance = useMemo(
    () => luminance(rgbColor.r, rgbColor.g, rgbColor.b),
    [rgbColor]
  );
  const whiteLuminance = useMemo(
    () => luminance(whiteRgb?.r ?? 0, whiteRgb?.g ?? 0, whiteRgb?.b ?? 0),
    [whiteRgb]
  );

  const ratio = useMemo(
    () =>
      tagLuminance > whiteLuminance
        ? (whiteLuminance + 0.05) / (tagLuminance + 0.05)
        : (tagLuminance + 0.05) / (whiteLuminance + 0.05),
    [tagLuminance, whiteLuminance]
  );

  const textColor = ratio < threshold ? WHITE : BLACK;

  return {
    textColor,
    bgColor: transformedColor ? color : FALLBACK_COLOR,
  };
}
